import {useCallback, useRef} from 'react'
import {useMutation} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {DesignerAction, LayoutSchema, UpsertFormLayout} from '@/types/form-layout'

export interface ReorderRequest {
    nextSchema: LayoutSchema
    baseline: LayoutSchema
}

export interface UseLayoutReorderOptions {
    useCaseKey: string
    displayName: string
    active: boolean
    version: number | null
    dispatch: (action: DesignerAction) => void
}

export interface UseLayoutReorderResult {
    reorder: (req: ReorderRequest) => void
    isReordering: boolean
    /**
     * Captures the current schema as the pre-drag baseline for a subsequent
     * reorder. Call from `onDragStart`. The captured value is consumed on the
     * next `reorder()` call so the same baseline survives across the optimistic
     * dispatch and the eventual REVERT on PUT failure.
     */
    captureBaseline: (schema: LayoutSchema) => void
    getCapturedBaseline: () => LayoutSchema | null
}

/**
 * Reducer-routed DnD reorder hook for the form-layout designer.
 *
 * WHY: a second drag fired while the first reorder PUT is still in flight
 * would corrupt the revert payload if the baseline were derived from the
 * current draft at revert time. Capturing the pre-drag schema in a ref and
 * passing it as the action payload pins the baseline to the drag that started
 * the round-trip.
 */
export function useLayoutReorder(options: UseLayoutReorderOptions): UseLayoutReorderResult {
    const {useCaseKey, displayName, active, version, dispatch} = options
    const services = useServices()
    const baselineRef = useRef<LayoutSchema | null>(null)

    const reorderMutation = useMutation({
        mutationFn: ({nextSchema}: ReorderRequest): Promise<unknown> => {
            const request: UpsertFormLayout = {
                displayName,
                layoutSchema: nextSchema,
                active,
                version,
            }
            return services.formLayouts.upsert(useCaseKey, request)
        },
        onError: (_error, variables) => {
            dispatch({type: 'LAYOUT_REORDER_REVERT', baseline: variables.baseline})
        },
    })

    const reorder = useCallback(
        (req: ReorderRequest) => {
            dispatch({type: 'LAYOUT_REORDER_OPTIMISTIC', nextSchema: req.nextSchema})
            reorderMutation.mutate(req)
        },
        [dispatch, reorderMutation],
    )

    const captureBaseline = useCallback((schema: LayoutSchema) => {
        baselineRef.current = schema
    }, [])

    const getCapturedBaseline = useCallback((): LayoutSchema | null => {
        return baselineRef.current
    }, [])

    return {
        reorder,
        isReordering: reorderMutation.isPending,
        captureBaseline,
        getCapturedBaseline,
    }
}
